import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Loading from './Loading';
import {
  getAverageRatingReview,
  getCourse,
  getInstructorCourses,
} from '../services/operations/courseApi';
import { formattedDate } from '../utils/dateFormatter';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { MdLanguage } from 'react-icons/md';
import CourseBuyingCard from '../components/core/coursePage/CourseBuyingCard';
import { ACCOUNT_TYPE } from '../utils/constants';
import { FaShare } from 'react-icons/fa';
import { setIsPresentInWishlist } from '../redux/slices/wishlistSlice';
import toast from 'react-hot-toast';
import copy from 'copy-to-clipboard';
import CourseContent from '../components/core/coursePage/CourseContent';
import Footer from '../components/common/Footer';
import CourseSlider from '../components/core/categoryPage/CourseSlider';
import StarRatings from 'react-star-ratings';

const CoursePage = () => {
  const { user } = useSelector(state => state.profile);
  const [course, setCourse] = useState(null);
  const [relativeCoruse, setRelativeCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avgRating, setAvgRating] = useState(0);
  const { courseId } = useParams();
  const dispatch = useDispatch();
  const handlerShareButton = () => {
    copy(window.location.href);
    toast('Link copied to clipboard', {
      icon: '📎',
    });
  };

  useEffect(() => {
    const fetchCourseDetail = async () => {
      setLoading(true);
      try {
        const response = await getCourse(courseId);
        const relativeCourseOfSameInstructor = await getInstructorCourses(
          response?.instructor?._id
        );
        dispatch(getAverageRatingReview(setAvgRating, response?._id));
        setCourse(response);
        setRelativeCourse(
          relativeCourseOfSameInstructor?.data?.courses.filter(
            course => course._id !== response._id
          )
        );
        dispatch(setIsPresentInWishlist(response));
      } catch (error) {
        console.log(
          'error while fetching course details in CoursePage component',
          error.message
        );
      } finally {
        setLoading(false);
      }
    };
    fetchCourseDetail();
  }, [courseId, dispatch]);

  if (loading) {
    return (
      <div className='h-screen w-screen'>
        <Loading />
      </div>
    );
  }

  return (
    <div className='mt-14 text-white'>
      <div className='relative'>
        <div className='lg:w-11/12 lg:max-w-maxContent lg:mx-auto lg:relative'>
          {user?.accountType !== ACCOUNT_TYPE.INSTRUCTOR ? (
            <div className='lg:absolute lg:right-0 lg:top-[2rem]'>
              <CourseBuyingCard
                course={course}
                user={user}
                courseId={courseId}
                handlerShareButton={handlerShareButton}
              />
            </div>
          ) : (
            <div className='lg:absolute lg:right-0 lg:top-[2rem] xl:w-[28rem] lg:w-[23rem] w-full lg:rounded-lg lg:bg-richblack-700 bg-richblack-800 p-4 lg:block sm:flex sm:justify-evenly'>
              <img
                className='object-cover lg:w-full sm:w-[60%] w-full rounded-xl xl:h-[17rem] lg:h-[12rem] h-[20rem]'
                loading='lazy'
                src={course?.thumbnail}
                alt={course?.courseName}
              />
              <div className='lg:flex items-center justify-between px-5'>
                <p className='text-3xl font-bold my-4'>
                  Rs. {parseInt(course?.price).toLocaleString()}
                </p>
                <button
                  type='button'
                  className='flex items-center text-xl gap-2 text-yellow-50 font-bold transition-all duration-200 hover:scale-[.98]'
                  onClick={handlerShareButton}
                >
                  <FaShare /> Share
                </button>
              </div>
            </div>
          )}
        </div>
        <section className='bg-richblack-800 py-10 md:py-16'>
          <div className='w-11/12 max-w-maxContent mx-auto'>
            <header className='text-3xl font-semibold my-3'>
              {course?.courseName}
            </header>
            <p className='text-richblack-300 lg:w-[60%] w-full my-3'>
              {course?.courseDescription}
            </p>
            <div className='flex flex-wrap items-center gap-y-3 py-3'>
              {avgRating > 0 && (
                <span className='text-yellow-50 text-xl pr-2'>
                  {avgRating.toFixed(1)}
                </span>
              )}
              <StarRatings
                numberOfStars={5}
                rating={avgRating}
                starDimension={'24px'}
                starSpacing={'1px'}
                starRatedColor={'#FFD60A'}
                starEmptyColor={'#6E727F'}
              />
              <span className='px-3 text-xl text-richblack-100'>
                ( {course?.ratingAndReviews?.length} review(s) )
              </span>
              <span className='text-lg'>
                Enrolled Students {course?.studentsEnrolled.length}
              </span>
            </div>
            <p className='my-3'>
              Created By
              <Link
                to={`/instructor/${course?.instructor?._id}`}
                className='mx-1 p-1 text-caribbeangreen-200 underline'
              >
                {course?.instructor?.firstName.toUpperCase()}{' '}
                {course?.instructor?.lastName.toUpperCase()}
              </Link>
            </p>
            <div className='flex flex-wrap items-center gap-4 my-5 text-lg'>
              <span className='flex gap-2 items-center'>
                <AiOutlineExclamationCircle /> Created At{' '}
                {formattedDate(course?.createdAt)}
              </span>{' '}
              ||
              <span className='flex gap-2 items-center'>
                <MdLanguage /> {course?.courseLanguage}{' '}
              </span>
            </div>
          </div>
        </section>
        <section className='w-11/12 max-w-maxContent mx-auto my-10'>
          <div className='border-2 lg:w-[60%] w-full rounded-sm p-5 border-richblack-700'>
            <header className='text-2xl font-bold mb-3'>
              What you'll learn
            </header>
            <p className='text-richblack-100 my-2'>{course?.whatYouWillLearn}</p>
          </div>
        </section>
        <section className='w-11/12 max-w-maxContent mx-auto my-4'>
          <CourseContent course={course} />
        </section>
      </div>
      <section className='w-11/12 max-w-maxContent mx-auto my-10'>
        <header className='text-3xl font-bold mb-3'>Instructor</header>
        <Link
          to={`/instructor/${course?.instructor?._id}`}
          className='flex items-center gap-5 my-5'
        >
          <img
            src={course?.instructor?.image}
            alt={course?.instructor?.accountType}
            loading='lazy'
            className='aspect-square w-[50px] rounded-full object-cover'
          />
          <div className='text-lg text-caribbeangreen-200'>
            {course?.instructor?.firstName.toUpperCase()}{' '}
            {course?.instructor?.lastName.toUpperCase()}
          </div>
        </Link>
      </section>
      {relativeCoruse?.length > 0 && (
        <section className='w-11/12 max-w-maxContent mx-auto xl:my-20 my-16 text-center'>
          <header className='text-3xl font-bold mb-3'>
            More Courses by{' '}
            {course?.instructor?.firstName.toUpperCase()}{' '}
            {course?.instructor?.lastName.toUpperCase()}
          </header>
          <div className='my-10 text-start'>
            <CourseSlider courses={relativeCoruse} />
          </div>
        </section>
      )}
      <div>
        <Footer />
      </div>
    </div>
  );
};

export default CoursePage;